import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import AirportAutocomplete from '../../components/feature/AirportAutocomplete';
import HotelDestinationAutocomplete from '../../components/feature/HotelDestinationAutocomplete';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';

export default function Home() {
  const navigate = useNavigate();

  // Search state
  const [searchTab, setSearchTab] = useState<'flights' | 'hotels'>('flights');
  const [flightType, setFlightType] = useState('roundtrip');
  const [flightClass, setFlightClass] = useState('economy');
  const [flightFromDisplay, setFlightFromDisplay] = useState('Lagos (LOS)');
  const [flightFromIata, setFlightFromIata] = useState('LOS');
  const [flightToDisplay, setFlightToDisplay] = useState('London (LHR)');
  const [flightToIata, setFlightToIata] = useState('LHR');

  // Dates default: tomorrow and 7 days later
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const nextWeek = new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0];
  const [flightDate, setFlightDate] = useState(tomorrow);
  const [returnDate, setReturnDate] = useState(nextWeek);

  // Hotel search state
  const [hotelDestination, setHotelDestination] = useState('Dubai, UAE');
  const [hotelCityId, setHotelCityId] = useState<number | undefined>(4567);
  const [checkIn, setCheckIn] = useState(tomorrow);
  const [checkOut, setCheckOut] = useState(nextWeek);

  // Travelers state
  const [travelers, setTravelers] = useState({ adults: 1, children: 0, rooms: 1 });
  const [showTravelers, setShowTravelers] = useState(false);

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.append('type', searchTab);

    if (searchTab === 'flights') {
      const fromCode = flightFromIata || flightFromDisplay.match(/\(([A-Z]{3})\)/)?.[1] || flightFromDisplay || 'LOS';
      const toCode = flightToIata || flightToDisplay.match(/\(([A-Z]{3})\)/)?.[1] || flightToDisplay || 'LHR';

      params.append('from', fromCode);
      params.append('origin', fromCode);
      params.append('to', toCode);
      params.append('destination', toCode);
      params.append('flightType', flightType);
      params.append('adults', travelers.adults.toString());
      params.append('date', flightDate || tomorrow);
      if (flightType === 'roundtrip' && returnDate) {
        params.append('return_date', returnDate);
        params.append('returnDate', returnDate);
      }
      params.append('cabin_class', flightClass);
      params.append('class', flightClass);
    } else {
      const dest = hotelDestination || 'Dubai, UAE';
      params.append('destination', dest);
      params.append('to', dest);
      if (hotelCityId) {
        params.append('city_id', hotelCityId.toString());
      }
      params.append('checkIn', checkIn || tomorrow);
      if (checkOut) params.append('checkOut', checkOut);
      params.append('rooms', travelers.rooms.toString());
      params.append('guests', travelers.adults.toString());
      params.append('adults', travelers.adults.toString());
    }

    navigate(`/search?${params.toString()}`);
  };

  const swapLocations = () => {
    const tempDisplay = flightFromDisplay;
    const tempIata = flightFromIata;
    setFlightFromDisplay(flightToDisplay);
    setFlightFromIata(flightToIata);
    setFlightToDisplay(tempDisplay);
    setFlightToIata(tempIata);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />

      {/* Hero Section with Dedicated Flight & Hotel Search */}
      <section
        className="relative min-h-[90vh] flex items-center justify-center bg-cover bg-center bg-no-repeat py-20"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.55), rgba(90, 74, 56, 0.45)), url('https://readdy.ai/api/search-image?query=Majestic%20view%20of%20the%20Prophets%20Mosque%20in%20Medina%20with%20iconic%20green%20dome%20and%20minarets%2C%20beautiful%20Islamic%20architecture%20at%20golden%20hour%2C%20pilgrims%20in%20courtyard%2C%20spiritual%20atmosphere%20with%20warm%20lighting%2C%20detailed%20architectural%20features%20of%20Masjid%20an-Nabawi%2C%20peaceful%20religious%20scene%20with%20elegant%20composition&width=1920&height=1080&seq=medina-hero&orientation=landscape')`
        }}
      >
        {/* Decorative overlay gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70 pointer-events-none"></div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4">
          {/* Logo and Tagline */}
          <div className="text-center mb-10">
            <img
              src="https://static.readdy.ai/image/3d693276a5424479ffa5f1cc868e7c58/99585d34dbb7c267061331244ebc5e82.png"
              alt="Rigland Travels"
              className="mx-auto mb-4 h-16 md:h-20 w-auto drop-shadow-2xl"
            />
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3 tracking-tight">
              Book Flights & Luxury Hotels Worldwide
            </h1>
            <p className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto font-medium">
              Explore global airlines, sacred journeys, and premium stays with real-time availability and transparent rates
            </p>
          </div>

          {/* Search Card - Luxury Glassmorphism UI */}
          <div className="bg-slate-900/85 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl overflow-visible">

            {/* Tab Navigation */}
            <div className="flex border-b border-white/10">
              <button
                type="button"
                onClick={() => setSearchTab('flights')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-4 px-6 text-sm font-bold tracking-wide transition-all cursor-pointer rounded-tl-3xl ${
                  searchTab === 'flights'
                    ? 'bg-white/15 text-white border-b-2 border-[#b8a080] shadow-sm'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <i className="ri-flight-takeoff-line text-xl text-[#b8a080]"></i>
                <span>Search Flights</span>
              </button>
              <button
                type="button"
                onClick={() => setSearchTab('hotels')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-4 px-6 text-sm font-bold tracking-wide transition-all cursor-pointer rounded-tr-3xl ${
                  searchTab === 'hotels'
                    ? 'bg-white/15 text-white border-b-2 border-[#b8a080] shadow-sm'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <i className="ri-hotel-line text-xl text-[#b8a080]"></i>
                <span>Find Hotels</span>
              </button>
            </div>

            {/* Search Body */}
            <div className="p-6 md:p-8">

              {/* ─── FLIGHTS TAB ─── */}
              {searchTab === 'flights' && (
                <div className="space-y-6">
                  {/* Flight Type & Cabin Class Controls */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pb-1">
                    <div className="flex items-center gap-6">
                      {['roundtrip', 'oneway'].map((type) => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-200 hover:text-white transition-colors">
                          <input
                            type="radio"
                            name="flightType"
                            value={type}
                            checked={flightType === type}
                            onChange={(e) => setFlightType(e.target.value)}
                            className="accent-[#b8a080] w-4 h-4"
                          />
                          {type === 'roundtrip' ? 'Round Trip' : 'One Way'}
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 uppercase font-semibold">Cabin:</span>
                      <select
                        value={flightClass}
                        onChange={(e) => setFlightClass(e.target.value)}
                        className="bg-black/40 border border-white/20 text-white text-xs font-semibold rounded-lg px-3 py-1.5 cursor-pointer focus:ring-1 focus:ring-[#b8a080] focus:border-[#b8a080] outline-none"
                      >
                        <option value="economy" className="bg-gray-900 text-white">Economy</option>
                        <option value="premium-economy" className="bg-gray-900 text-white">Premium Economy</option>
                        <option value="business" className="bg-gray-900 text-white">Business Class</option>
                        <option value="first" className="bg-gray-900 text-white">First Class</option>
                      </select>
                    </div>
                  </div>

                  {/* Origin / Swap / Destination Row */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                    <AirportAutocomplete
                      label="Flying From (Airport / City)"
                      value={flightFromDisplay}
                      onChange={(display, iata) => {
                        setFlightFromDisplay(display);
                        setFlightFromIata(iata);
                      }}
                      placeholder="e.g. Lagos (LOS), London (LHR)"
                      icon="ri-flight-takeoff-line"
                    />

                    <button
                      type="button"
                      onClick={swapLocations}
                      className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-[#b8a080] hover:bg-[#c8b090] text-white transition-all cursor-pointer shadow-lg self-end mb-0.5 group"
                      title="Swap Departure and Destination"
                    >
                      <i className="ri-arrow-left-right-line text-lg group-hover:rotate-180 transition-transform duration-300"></i>
                    </button>

                    <AirportAutocomplete
                      label="Flying To (Airport / City)"
                      value={flightToDisplay}
                      onChange={(display, iata) => {
                        setFlightToDisplay(display);
                        setFlightToIata(iata);
                      }}
                      placeholder="e.g. Jeddah (JED), Dubai (DXB)"
                      icon="ri-flight-land-line"
                    />
                  </div>

                  {/* Dates & Travelers Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                        Departure Date
                      </label>
                      <Input
                        type="date"
                        value={flightDate}
                        onChange={(e) => setFlightDate(e.target.value)}
                        icon="ri-calendar-line"
                      />
                    </div>

                    {flightType === 'roundtrip' && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                          Return Date
                        </label>
                        <Input
                          type="date"
                          value={returnDate}
                          onChange={(e) => setReturnDate(e.target.value)}
                          icon="ri-calendar-line"
                        />
                      </div>
                    )}

                    <div className="relative">
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                        Passengers
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowTravelers(!showTravelers)}
                        className="w-full flex items-center gap-2 px-3.5 py-3 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-sm text-white font-medium cursor-pointer transition-colors"
                      >
                        <i className="ri-user-line text-[#b8a080]"></i>
                        <span>
                          {travelers.adults} Adult{travelers.adults > 1 ? 's' : ''}
                          {travelers.children > 0 ? `, ${travelers.children} Child` : ''}
                        </span>
                        <i className={`ri-arrow-down-s-line text-gray-400 ml-auto transition-transform ${showTravelers ? 'rotate-180' : ''}`}></i>
                      </button>

                      {showTravelers && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-gray-900 border border-white/20 rounded-2xl shadow-2xl p-4 z-50 space-y-4">
                          {[
                            { label: 'Adults (12+ yrs)', key: 'adults', min: 1 },
                            { label: 'Children (2-11 yrs)', key: 'children', min: 0 },
                          ].map(({ label, key, min }) => (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-200">{label}</span>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setTravelers(prev => ({ ...prev, [key]: Math.max(min, (prev as any)[key] - 1) }))}
                                  className="w-8 h-8 rounded-lg border border-white/20 bg-white/5 hover:bg-white/15 flex items-center justify-center cursor-pointer text-white"
                                >
                                  <i className="ri-subtract-line text-xs"></i>
                                </button>
                                <span className="w-6 text-center text-sm font-bold text-white">{(travelers as any)[key]}</span>
                                <button
                                  type="button"
                                  onClick={() => setTravelers(prev => ({ ...prev, [key]: (prev as any)[key] + 1 }))}
                                  className="w-8 h-8 rounded-lg border border-white/20 bg-white/5 hover:bg-white/15 flex items-center justify-center cursor-pointer text-white"
                                >
                                  <i className="ri-add-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                          <Button size="sm" fullWidth onClick={() => setShowTravelers(false)}>
                            Done
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ─── HOTELS TAB ─── */}
              {searchTab === 'hotels' && (
                <div className="space-y-6">
                  <div>
                    <HotelDestinationAutocomplete
                      label="Destination City or Hotel"
                      value={hotelDestination}
                      onChange={(name, id) => {
                        setHotelDestination(name);
                        setHotelCityId(id);
                      }}
                      placeholder="e.g. Dubai, Mecca, Medina, London, Lagos"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                        Check-in Date
                      </label>
                      <Input
                        type="date"
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                        icon="ri-calendar-line"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                        Check-out Date
                      </label>
                      <Input
                        type="date"
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        icon="ri-calendar-line"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                        Guests & Rooms
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowTravelers(!showTravelers)}
                        className="w-full flex items-center gap-2 px-3.5 py-3 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-sm text-white font-medium cursor-pointer transition-colors"
                      >
                        <i className="ri-user-line text-[#b8a080]"></i>
                        <span>
                          {travelers.adults} Guest{travelers.adults > 1 ? 's' : ''}, {travelers.rooms} Room{travelers.rooms > 1 ? 's' : ''}
                        </span>
                        <i className={`ri-arrow-down-s-line text-gray-400 ml-auto transition-transform ${showTravelers ? 'rotate-180' : ''}`}></i>
                      </button>

                      {showTravelers && (
                        <div className="absolute top-full left-0 mt-2 w-full bg-gray-900 border border-white/20 rounded-2xl shadow-2xl p-4 z-50 space-y-4">
                          {[
                            { label: 'Guests', key: 'adults', min: 1 },
                            { label: 'Rooms', key: 'rooms', min: 1 },
                          ].map(({ label, key, min }) => (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-200">{label}</span>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setTravelers(prev => ({ ...prev, [key]: Math.max(min, (prev as any)[key] - 1) }))}
                                  className="w-8 h-8 rounded-lg border border-white/20 bg-white/5 hover:bg-white/15 flex items-center justify-center cursor-pointer text-white"
                                >
                                  <i className="ri-subtract-line text-xs"></i>
                                </button>
                                <span className="w-6 text-center text-sm font-bold text-white">{(travelers as any)[key]}</span>
                                <button
                                  type="button"
                                  onClick={() => setTravelers(prev => ({ ...prev, [key]: (prev as any)[key] + 1 }))}
                                  className="w-8 h-8 rounded-lg border border-white/20 bg-white/5 hover:bg-white/15 flex items-center justify-center cursor-pointer text-white"
                                >
                                  <i className="ri-add-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                          <Button size="sm" fullWidth onClick={() => setShowTravelers(false)}>
                            Done
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Search Submit Button */}
              <div className="mt-8">
                <button
                  type="button"
                  onClick={handleSearch}
                  className="w-full bg-gradient-to-r from-[#b8a080] via-[#c4ae90] to-[#8a7060] hover:from-[#c8b090] hover:via-[#d0ba9c] hover:to-[#9a8070] text-white font-bold py-4 rounded-2xl text-base tracking-wider uppercase transition-all shadow-xl shadow-[#b8a080]/30 hover:shadow-2xl hover:shadow-[#b8a080]/40 cursor-pointer flex items-center justify-center gap-3 transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  <i className="ri-search-2-line text-xl"></i>
                  <span>Find {searchTab === 'flights' ? 'Flights' : 'Hotels'}</span>
                </button>
              </div>

            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-gray-300 text-xs font-medium">
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
              <i className="ri-shield-check-line text-[#b8a080] text-sm"></i>
              <span>Secure Verified Booking</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
              <i className="ri-money-dollar-circle-line text-[#b8a080] text-sm"></i>
              <span>Competitive Rates</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
              <i className="ri-customer-service-2-line text-[#b8a080] text-sm"></i>
              <span>24/7 Dedicated Support</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
              <i className="ri-flight-takeoff-line text-[#b8a080] text-sm"></i>
              <span>Global Airline Coverage</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Key Stats Section */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-stone-900 to-gray-900 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#b8a080] mb-1">50K+</div>
              <div className="text-gray-400 text-xs md:text-sm font-medium">Hajj & Umrah Pilgrims</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#b8a080] mb-1">200K+</div>
              <div className="text-gray-400 text-xs md:text-sm font-medium">Happy Travelers</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#b8a080] mb-1">500+</div>
              <div className="text-gray-400 text-xs md:text-sm font-medium">Global Airlines & Hotels</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#b8a080] mb-1">24/7</div>
              <div className="text-gray-400 text-xs md:text-sm font-medium">Travel Assistance</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid (Clean overview without book now cards) */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white text-center mb-2">Our Premium Travel Services</h2>
          <p className="text-gray-400 text-sm text-center mb-10 max-w-lg mx-auto">
            Comprehensive travel management tailored for pilgrims, corporate executives, and leisure travelers
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-flight-takeoff-line', label: 'Worldwide Flights', desc: 'Domestic & International airlines', link: '/search?type=flights' },
              { icon: 'ri-hotel-line', label: 'Luxury Hotels', desc: 'Best rates in Saudi Arabia & worldwide', link: '/search?type=hotels' },
              { icon: 'ri-building-line', label: 'Hajj & Umrah', desc: 'Guided sacred pilgrimage packages', link: '/hajj-umrah' },
              { icon: 'ri-taxi-line', label: 'Airport Transfers', desc: 'Chauffeur & taxi transport', link: '/search?type=cars' },
            ].map((service) => (
              <a
                key={service.label}
                href={service.link}
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#b8a080]/50 rounded-2xl p-6 text-center transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-[#b8a080]/15 rounded-2xl flex items-center justify-center group-hover:bg-[#b8a080]/30 transition-colors">
                  <i className={`${service.icon} text-2xl text-[#b8a080]`}></i>
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{service.label}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{service.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
